import {useQuery} from "@tanstack/react-query";
import {useEffect, useState} from "react";

import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {vehiclesService} from "../services";
import type {Vehicle} from "../types";

const DEBOUNCE_MS = 800;

export function useVehicleByPlate(plate: string, enabled: boolean) {
  const userProperty = useStoredUserProperty();
  const trimmed = plate?.trim() ?? "";
  const [debouncedPlate, setDebouncedPlate] = useState(trimmed);

  useEffect(() => {
    if (!trimmed) {
      setDebouncedPlate("");
      return;
    }
    const t = setTimeout(() => setDebouncedPlate(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [trimmed]);

  const queryEnabled = Boolean(enabled && debouncedPlate && userProperty?.id);

  const query = useQuery({
    queryKey: ["vehicles", "by-plate", debouncedPlate, userProperty?.id],
    queryFn: () =>
      vehiclesService.getVehicles({
        filter: {
          property_id: userProperty!.id,
          plate: debouncedPlate,
        },
        page: {limit: 1, offset: 0},
      }),
    enabled: queryEnabled,
  });

  /** When `enabled` is false (e.g. edit mode), ignore cached rows — vehicles invalidation refetches by-plate and would otherwise surface the vehicle being edited as a "duplicate". */
  const vehicle: Vehicle | null =
    queryEnabled && query.data?.data?.length ? (query.data.data[0] ?? null) : null;

  return {
    vehicle,
    isSearching: query.isFetching,
  };
}
